import { useRef, useState, useEffect } from 'react';
import useAuth from '@/hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import useInput from '@/hooks/useInput';
import useToggle from '@/hooks/useToggle';
import useRefreshToken from '@/hooks/useRefreshToken';
import { Card } from '@/components/ui/card';


// import githubImg from '@/../assets/img/github.svg';
// import googleImg from '@/../assets/img/google.svg';

const Login = () => {
  const { logIn } = useAuth();

  const refresh = useRefreshToken();

  const navigate = useNavigate();
  const location = useLocation();

  const userRef: any = useRef();
  const errRef: any = useRef();

  const [user, resetUser, userAttribs] = useInput('user', '')
  const [password, setPassword] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [check, toggleCheck] = useToggle('persist', false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    userRef.current.focus();
  }, [])

  useEffect(() => {
    setErrMsg('');
  }, [user, password])

  // Función para manejar el diálogo de sesiones máximas
  const handleMaxSessions = () => {
    return new Promise((resolve) => {
      // Crear elementos del diálogo
      const modalContainer = document.createElement('div');
      modalContainer.className = 'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50';
      
      const modalContent = document.createElement('div');
      modalContent.className = 'bg-white rounded-lg shadow-lg max-w-md w-full p-6';
      
      const modalTitle = document.createElement('h3');
      modalTitle.className = 'text-lg font-bold mb-4 text-slate-800';
      modalTitle.textContent = 'Advertencia';
      
      const modalText = document.createElement('p');
      modalText.className = 'mb-6 text-slate-600';
      modalText.textContent = 'Has alcanzado el número máximo de sesiones activas';
      
      const buttonContainer = document.createElement('div');
      buttonContainer.className = 'flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2 justify-end';
      
      const cancelButton = document.createElement('button');
      cancelButton.className = 'py-2 px-4 bg-slate-200 text-slate-800 rounded hover:bg-slate-300';
      cancelButton.textContent = 'Cancelar';
      cancelButton.onclick = () => {
        document.body.removeChild(modalContainer);
        resolve('cancel');
      };
      
      const closeOldestButton = document.createElement('button');
      closeOldestButton.className = 'py-2 px-4 bg-blue-500 text-white rounded hover:bg-blue-600';
      closeOldestButton.textContent = 'Cerrar sesión más antigua';
      closeOldestButton.onclick = () => {
        document.body.removeChild(modalContainer);
        resolve('closeOldest');
      };
      
      const closeAllButton = document.createElement('button');
      closeAllButton.className = 'py-2 px-4 bg-red-500 text-white rounded hover:bg-red-600';
      closeAllButton.textContent = 'Cerrar todas las sesiones';
      closeAllButton.onclick = () => {
        document.body.removeChild(modalContainer);
        resolve('closeAll');
      };
      
      // Ensamblar el diálogo
      buttonContainer.appendChild(cancelButton);
      buttonContainer.appendChild(closeOldestButton);
      buttonContainer.appendChild(closeAllButton);
      
      modalContent.appendChild(modalTitle);
      modalContent.appendChild(modalText);
      modalContent.appendChild(buttonContainer);
      
      modalContainer.appendChild(modalContent);
      
      // Mostrar el diálogo
      document.body.appendChild(modalContainer);
    });
  };

  const loginWithSessionMode = async (sessionCloseMode?: 'closeOldest' | 'closeAll') => {
    try {
      setIsSubmitting(true);
      
      // Si tenemos un modo de cierre de sesión, lo incluimos en la petición
      const data = await logIn(user, password, sessionCloseMode);
      
      const sessionSpecificDefaultUrl = data.principalType === 'Company'
        ? '/shop/reports/eod'
        : data.principalType === "user"
          ? '/admin/companies'
          : data.principalType === 'Provider'
            ? '/portal'
            : '/fleet';

      const from = location.state?.from?.pathname || sessionSpecificDefaultUrl;
      await refresh();

      resetUser();
      setPassword('');
      navigate(from, { replace: true });
    } catch (err: any) {
      console.log('[login err]', err);

      if (!err?.data) {
        setErrMsg('Sin respuesta del servidor');
      } else if (err.data?.httpStatus === 400) {
        setErrMsg('Falta email o contraseña');
      } else if (err.data?.httpStatus === 401) {
        setErrMsg('Contraseña incorrecta');
      } else if (err.data?.httpStatus === 409) {
        // Manejo de sesiones máximas
        const result = await handleMaxSessions();
        if (result !== 'cancel') {
          // Si el usuario eligió cerrar sesiones, intentamos nuevamente con el modo elegido
          return loginWithSessionMode(result as 'closeOldest' | 'closeAll');
        }
      } else {
        setErrMsg('Falló la sesión');
      }

      errRef.current.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: { preventDefault: () => void; }) => {
    e.preventDefault();
    await loginWithSessionMode();
  }

  return (

    <div className="container mx-auto px-4 h-full">
      <div className="flex content-center items-center justify-center h-full">
        <div className="w-full lg:w-4/12 px-4">
          <Card variant="legacy" className="mb-6">
            <div className="rounded-t mb-0 px-6 py-6">
              <div className="text-center mb-3">
                <h6 className="text-slate-500 text-sm font-bold">
                  Iniciar Sesión
                </h6>
              </div>
              {/* <div className="btn-wrapper text-center"> */}
              {/*   <button */}
              {/*     className="bg-white active:bg-slate-50 text-slate-700 px-4 py-2 rounded outline-none focus:outline-none mr-2 mb-1 uppercase shadow hover:shadow-md inline-flex items-center font-bold text-xs ease-linear transition-all duration-150" */}
              {/*     type="button" */}
              {/*   > */}
              {/*     <img */}
              {/*       alt="..." */}
              {/*       className="w-5 mr-1" */}
              {/*       src={githubImg} */}
              {/*     /> */}
              {/*     Github */}
              {/*   </button> */}
              {/*   <button */}
              {/*     className="bg-white active:bg-slate-50 text-slate-700 px-4 py-2 rounded outline-none focus:outline-none mr-1 mb-1 uppercase shadow hover:shadow-md inline-flex items-center font-bold text-xs ease-linear transition-all duration-150" */}
              {/*     type="button" */}
              {/*   > */}
              {/*     <img */}
              {/*       alt="..." */}
              {/*       className="w-5 mr-1" */}
              {/*       src={googleImg} */}
              {/*     /> */}
              {/*     Google */}
              {/*   </button> */}
              {/* </div> */}
              <hr className="mt-6 border-b-1 border-slate-300" />
            </div>
            <div className="flex-auto px-4 lg:px-10 py-10 pt-0">
              {/* <div className="text-slate-400 text-center mb-3 font-bold"> */}
              {/*   <small>Or sign in with credentials</small> */}
              {/* </div> */}
              <form onSubmit={handleSubmit}>
                <div className="relative w-full mb-3">
                  <label
                    className="block uppercase text-slate-600 text-xs font-bold mb-2"
                    htmlFor="username"
                  >
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    className="border-0 px-3 py-3 placeholder-slate-300 text-slate-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                    placeholder="Username"
                    id='username'
                    ref={userRef}
                    autoComplete='off'
                    {...userAttribs}
                  />
                </div>

                <div className="relative w-full mb-3">
                  <label
                    className="block uppercase text-slate-600 text-xs font-bold mb-2"
                    htmlFor="password"
                  >
                    Contraseña
                  </label>
                  <input
                    id='password'
                    type="password"
                    className="border-0 px-3 py-3 placeholder-slate-300 text-slate-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                    placeholder="Password"
                    onChange={(e) => setPassword(e.target.value)}
                    value={password}
                    required
                  />
                </div>
                <div>
                  <label
                    className="inline-flex items-center cursor-pointer"
                    htmlFor='persist'>
                    <input
                      type="checkbox"
                      className="form-checkbox border-0 rounded text-slate-700 ml-1 w-5 h-5 ease-linear transition-all duration-150"
                      id='persist'
                      onChange={toggleCheck}
                      checked={check}
                    />
                    <span className="ml-2 text-sm font-semibold text-slate-600">
                      Recordar mis datos
                    </span>
                  </label>
                </div>

                <div className="text-center mt-6">
                  <button
                    className="bg-slate-800 text-white active:bg-slate-600 text-sm font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 w-full ease-linear transition-all duration-150"
                    type='submit'
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Iniciando...' : 'Iniciar'}
                  </button>

                  <p ref={errRef} className={errMsg ? 'errmsg' : 'offscreen'} aria-live='assertive'>{errMsg}</p>

                </div>
              </form>
            </div>
          </Card>
          {/* <div className="flex flex-wrap mt-6 relative"> */}
          {/*   <div className="w-1/2"> */}
          {/*     <button */}
          {/*       onClick={(e) => e.preventDefault()} */}
          {/*       className="text-slate-200" */}
          {/*     > */}
          {/*       <small>Forgot password?</small> */}
          {/*     </button> */}
          {/*   </div> */}
          {/*   <div className="w-1/2 text-right"> */}
          {/*     <Link to="/auth/register" className="text-slate-200"> */}
          {/*       <small>Create new account</small> */}
          {/*     </Link> */}
          {/*   </div> */}
          {/* </div> */}
        </div>
      </div>
    </div>

  )
}

export default Login
